import { useState, useEffect } from 'react';
import { Mic, PenTool, X, User, Phone, MapPin, FileText, ChevronDown, ChevronUp, Building2, Calendar, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import CompactVoiceForm from './CompactVoiceForm';

type ClientType = 'PRIVATE' | 'COMPANY';

interface EditClientData {
  id: string;
  clientType?: ClientType;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  whatsappNumber?: string;
  fiscalCode?: string;
  vatNumber?: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  birthDate?: string;
  birthPlace?: string;
  notes?: string;
  // Campi azienda
  companyName?: string;
  legalAddress?: string;
  legalCity?: string;
  legalProvince?: string;
  legalPostalCode?: string;
  operationalAddress?: string;
  operationalCity?: string;
  operationalProvince?: string;
  operationalPostalCode?: string;
  sdiCode?: string;
  pec?: string;
  legalRepFirstName?: string;
  legalRepLastName?: string;
  legalRepFiscalCode?: string;
  _validationErrors?: Record<string, string>;
}

interface NewClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  editClient?: EditClientData;
  /** Dati da contatto Google (apre form manuale precompilato) */
  prefillFromGoogle?: Partial<EditClientData>;
}

export default function NewClientModal({ isOpen, onClose, editClient, prefillFromGoogle }: NewClientModalProps) {
  const [clientType, setClientType] = useState<ClientType | null>(editClient?.clientType || null);
  const [selectedMode, setSelectedMode] = useState<string | null>(editClient ? 'manual' : null);
  const [voiceExtractedData, setVoiceExtractedData] = useState<any>(null);

  const googlePrefill = prefillFromGoogle as EditClientData | undefined;

  // Reset state quando il modal si chiude
  useEffect(() => {
    if (!isOpen) {
      setClientType(editClient?.clientType || null);
      setSelectedMode(editClient ? 'manual' : null);
      setVoiceExtractedData(null);
    } else if (googlePrefill && !editClient) {
      setClientType(googlePrefill.clientType || 'PRIVATE');
      setSelectedMode('manual');
      setVoiceExtractedData(googlePrefill);
    }
  }, [isOpen, editClient, googlePrefill]);

  const modes = [
    {
      id: 'manual',
      name: 'Inserimento Manuale',
      description: 'Compila il form con i dati del cliente',
      icon: PenTool,
      color: 'bg-blue-500',
    },
    {
      id: 'voice',
      name: 'Inserimento Vocale',
      description: 'Interfaccia con campi sempre modificabili',
      icon: Mic,
      color: 'bg-green-500',
    },
  ];

  const handleModeSelect = (modeId: string) => {
    setSelectedMode(modeId);
  };

  const handleBack = () => {
    if (selectedMode) {
      setSelectedMode(null);
    } else if (clientType) {
      setClientType(null);
    }
  };

  const renderModeContent = () => {
    switch (selectedMode) {
      case 'manual':
        if (clientType === 'COMPANY') {
          return <CompanyClientForm onSuccess={() => {
            setSelectedMode(null);
            setClientType(null);
            onClose();
          }} editData={editClient || googlePrefill || voiceExtractedData} />;
        }
        return <ManualClientForm onSuccess={() => {
          setSelectedMode(null);
          setClientType(null);
          onClose();
        }} editData={editClient || googlePrefill || voiceExtractedData} />;

      case 'voice':
        return (
          <CompactVoiceForm 
            onSuccess={() => {
              setSelectedMode(null);
              setClientType(null);
              onClose();
            }}
            onDataExtracted={(data) => {
              setVoiceExtractedData(data);
              setSelectedMode('manual');
            }}
          />
        );

      default:
        return null;
    }
  };

  const getTitle = () => {
    if (editClient) return 'Modifica Cliente';
    if (selectedMode) {
      return clientType === 'COMPANY' ? 'Nuova Azienda' : 'Nuovo Cliente Privato';
    }
    if (clientType) return 'Scegli Modalità di Inserimento';
    return 'Tipo Cliente';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        {selectedMode !== 'voice' && (
          <DialogHeader className="pb-2">
            <div className="flex items-center justify-between">
              {(clientType || selectedMode) && !editClient && (
                <Button variant="ghost" size="sm" onClick={handleBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Indietro
                </Button>
              )}
              <DialogTitle className="text-lg font-semibold flex-1 text-center">
                {getTitle()}
              </DialogTitle>
              {(clientType || selectedMode) && !editClient && <div className="w-20" />}
            </div>
          </DialogHeader>
        )}

        {/* Step 1: Selezione tipo cliente */}
        {!clientType && !editClient && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <Card
              className="cursor-pointer transition-all hover:shadow-lg hover:border-blue-500"
              onClick={() => setClientType('PRIVATE')}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Cliente Privato</h3>
                <p className="text-gray-600 text-sm">Persona fisica con dati anagrafici</p>
              </CardContent>
            </Card>
            <Card
              className="cursor-pointer transition-all hover:shadow-lg hover:border-purple-500"
              onClick={() => setClientType('COMPANY')}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-purple-500 rounded-full flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Azienda</h3>
                <p className="text-gray-600 text-sm">Società con sede legale e operativa</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Selezione modalità inserimento (solo per nuovi clienti) */}
        {clientType && !selectedMode && !editClient && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {modes.map((mode) => {
              const Icon = mode.icon;
              // Per aziende, solo inserimento manuale
              if (clientType === 'COMPANY' && mode.id === 'voice') return null;
              return (
                <Card
                  key={mode.id}
                  className="cursor-pointer transition-all hover:shadow-lg hover:border-blue-500"
                  onClick={() => handleModeSelect(mode.id)}
                >
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 ${mode.color} rounded-full flex items-center justify-center`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{mode.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{mode.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Step 3: Form effettivo */}
        {(selectedMode || editClient) && (
          <div className="mt-4">
            {renderModeContent()}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// FORM CLIENTE PRIVATO
// ============================================
function ManualClientForm({ onSuccess, editData }: {
  onSuccess: () => void;
  editData?: EditClientData;
}) {
  const [formData, setFormData] = useState({
    firstName: editData?.firstName || '',
    lastName: editData?.lastName || '',
    email: editData?.email || '',
    phone: editData?.phone || '',
    whatsappNumber: editData?.whatsappNumber || '',
    fiscalCode: editData?.fiscalCode || '',
    vatNumber: editData?.vatNumber || '',
    address: editData?.address || '',
    city: editData?.city || '',
    province: editData?.province || '',
    postalCode: editData?.postalCode || '',
    country: editData?.country || 'IT',
    birthDate: editData?.birthDate ? editData.birthDate.split('T')[0] : '',
    birthPlace: editData?.birthPlace || '',
    notes: editData?.notes || '',
  });

  const [expandedSections, setExpandedSections] = useState({
    anagrafica: true,
    contatti: true,
    residenza: false,
    fiscale: false,
    note: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [duplicateClients, setDuplicateClients] = useState<Array<any>>([]);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [lastDuplicateKey, setLastDuplicateKey] = useState('');
  
  useEffect(() => {
    if (editData?._validationErrors) {
      setErrors(editData._validationErrors);
      const sectionsWithErrors = new Set<string>();
      Object.keys(editData._validationErrors).forEach(field => {
        if (['firstName', 'lastName', 'birthDate', 'birthPlace'].includes(field)) {
          sectionsWithErrors.add('anagrafica');
        } else if (['email', 'phone', 'whatsappNumber'].includes(field)) {
          sectionsWithErrors.add('contatti');
        } else if (['address', 'city', 'province', 'postalCode', 'country'].includes(field)) {
          sectionsWithErrors.add('residenza');
        } else if (['fiscalCode', 'vatNumber'].includes(field)) {
          sectionsWithErrors.add('fiscale');
        } else if (['notes'].includes(field)) {
          sectionsWithErrors.add('note');
        }
      });
      
      if (sectionsWithErrors.size > 0) {
        setExpandedSections(prev => {
          const newSections = { ...prev };
          sectionsWithErrors.forEach(section => {
            newSections[section as keyof typeof prev] = true;
          });
          return newSections;
        });
      }
    }
  }, [editData?._validationErrors]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const validateFiscalCode = (cf: string): boolean => {
    if (!cf) return true;
    const regex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i;
    return regex.test(cf.toUpperCase());
  };

  const validateVatNumber = (vat: string): boolean => {
    if (!vat) return true;
    const regex = /^[0-9]{11}$/;
    return regex.test(vat);
  };

  const validateEmail = (email: string): boolean => {
    if (!email) return true;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true;
    const regex = /^(\+39)?[\s]?[0-9]{9,10}$/;
    return regex.test(phone.replace(/\s/g, ''));
  };

  const validatePostalCode = (cap: string): boolean => {
    if (!cap) return true;
    const regex = /^[0-9]{5}$/;
    return regex.test(cap);
  };

  const checkDuplicates = async () => {
    // In modifica non blocchiamo/avvisiamo: il caso d'uso è la creazione per evitare duplicati.
    if (editData?.id) return;

    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();

    const hasName = firstName.length > 0 && lastName.length > 0;
    const hasEmail = email.length > 0 && validateEmail(email);
    const hasPhone = phone.length > 0 && validatePhone(phone);

    if (!hasName && !hasEmail && !hasPhone) {
      setDuplicateClients([]);
      return;
    }

    const key = `${firstName.toLowerCase()}|${lastName.toLowerCase()}|${email.toLowerCase()}|${phone}`;
    if (key === lastDuplicateKey) return;
    setLastDuplicateKey(key);

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const params = new URLSearchParams();
    if (hasName) {
      params.set('firstName', firstName);
      params.set('lastName', lastName);
    }
    if (hasEmail) params.set('email', email);
    if (hasPhone) params.set('phone', phone);

    setIsCheckingDuplicates(true);
    try {
      const response = await fetch(`/api/clients/check-duplicates?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data?.success) {
        const candidates = (data.data?.clients || []) as Array<any>;
        // Se troviamo corrispondenze, mostriamo un avviso non bloccante
        setDuplicateClients(candidates);
      }
    } catch (e) {
      // Non blocchiamo la UX per un check best-effort
      console.error('check-duplicates error', e);
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'Nome obbligatorio';
    if (!formData.lastName.trim()) newErrors.lastName = 'Cognome obbligatorio';
    if (formData.email && !validateEmail(formData.email)) newErrors.email = 'Email non valida';
    if (formData.phone && !validatePhone(formData.phone)) newErrors.phone = 'Telefono non valido';
    if (formData.whatsappNumber && !validatePhone(formData.whatsappNumber)) newErrors.whatsappNumber = 'Numero WhatsApp non valido';
    if (formData.fiscalCode && !validateFiscalCode(formData.fiscalCode)) newErrors.fiscalCode = 'Codice Fiscale non valido';
    if (formData.vatNumber && !validateVatNumber(formData.vatNumber)) newErrors.vatNumber = 'Partita IVA non valida';
    if (formData.postalCode && !validatePostalCode(formData.postalCode)) newErrors.postalCode = 'CAP non valido';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Correggi gli errori nel form');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('accessToken');
      const payload: any = {
        clientType: 'PRIVATE',
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
      };

      if (formData.email) payload.email = formData.email.trim();
      if (formData.phone) payload.phone = formData.phone.trim();
      if (formData.whatsappNumber) payload.whatsappNumber = formData.whatsappNumber.trim();
      if (formData.fiscalCode) payload.fiscalCode = formData.fiscalCode.toUpperCase().trim();
      if (formData.vatNumber) payload.vatNumber = formData.vatNumber.trim();
      if (formData.address) payload.address = formData.address.trim();
      if (formData.city) payload.city = formData.city.trim();
      if (formData.province) payload.province = formData.province.toUpperCase().trim();
      if (formData.postalCode) payload.postalCode = formData.postalCode.trim();
      if (formData.country) payload.country = formData.country.trim();
      if (formData.birthDate) {
        const date = new Date(formData.birthDate);
        payload.birthDate = date.toISOString().split('T')[0];
      }
      if (formData.birthPlace) payload.birthPlace = formData.birthPlace.trim();
      if (formData.notes) payload.notes = formData.notes.trim();

      const url = editData?.id ? `/api/clients/${editData.id}` : '/api/clients';
      const method = editData?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(editData?.id ? 'Cliente aggiornato con successo!' : 'Cliente creato con successo!');
        onSuccess();
      } else {
        if (data.errors) {
          let fieldErrors: Record<string, string> = {};
          if (Array.isArray(data.errors)) {
            data.errors.forEach((error: string) => {
              if (error.includes('firstName') || error.includes('Nome')) fieldErrors.firstName = error;
              else if (error.includes('lastName') || error.includes('Cognome')) fieldErrors.lastName = error;
              else if (error.includes('email') || error.includes('Email')) fieldErrors.email = error;
              else if (error.includes('phone') || error.includes('Telefono')) fieldErrors.phone = error;
              else if (error.includes('fiscalCode') || error.includes('Codice Fiscale')) fieldErrors.fiscalCode = error;
              else if (error.includes('vatNumber') || error.includes('Partita IVA')) fieldErrors.vatNumber = error;
              else if (error.includes('whatsappNumber') || error.includes('WhatsApp')) fieldErrors.whatsappNumber = error;
              else if (error.includes('postalCode') || error.includes('CAP')) fieldErrors.postalCode = error;
              else fieldErrors.general = error;
            });
          } else {
            fieldErrors = data.errors;
          }
          setErrors(fieldErrors);
          toast.warning('Correggi gli errori evidenziati');
        } else {
          toast.error(data.message || 'Errore nella creazione del cliente');
        }
      }
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error('Errore nella creazione del cliente');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-4">
        <User className="mx-auto h-10 w-10 text-blue-500 mb-2" />
        <p className="text-gray-600 text-sm">
          {editData?.id ? 'Modifica i dati del cliente privato' : 'Inserisci i dati del nuovo cliente privato'}
        </p>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-sm">{errors.general}</p>
        </div>
      )}

      {!editData?.id && duplicateClients.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-yellow-900 text-sm font-medium">
            Possibili duplicati trovati
          </p>
          <p className="text-yellow-800 text-xs mt-1">
            Se è lo stesso cliente (anche con indirizzo cambiato), conviene riutilizzare il cliente esistente e aggiornare indirizzo/contatti.
          </p>
          <div className="mt-2 space-y-2">
            {duplicateClients.slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-start justify-between gap-2 rounded-md bg-white/60 p-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {c.firstName} {c.lastName}
                    {c._count?.practices ? (
                      <span className="ml-2 text-xs text-gray-600">
                        ({c._count.practices} pratiche)
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-gray-700">
                    {c.phone ? `Tel: ${c.phone}` : ''}{c.phone && c.email ? ' • ' : ''}{c.email ? `Email: ${c.email}` : ''}
                  </div>
                  {(c.address || c.city) && (
                    <div className="text-xs text-gray-600">
                      {c.address ? c.address : ''}{c.address && c.city ? ', ' : ''}{c.city ? c.city : ''}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onSuccess();
                      setTimeout(() => {
                        window.location.href = `/clients/${c.id}`;
                      }, 0);
                    }}
                  >
                    Apri
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="text-xs text-gray-600">
              {isCheckingDuplicates ? 'Verifica duplicati in corso…' : 'Avviso non bloccante'}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setDuplicateClients([])}
            >
              Ignora
            </Button>
          </div>
        </div>
      )}

      {/* Sezione Anagrafica */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection('anagrafica')}
        >
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-blue-600" />
            <h3 className="font-semibold text-sm">Anagrafica</h3>
            <Badge variant="destructive" className="text-xs">Obbligatorio</Badge>
          </div>
          {expandedSections.anagrafica ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.anagrafica && (
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName" className="text-sm">Nome *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  onBlur={checkDuplicates}
                  placeholder="Mario"
                  className={errors.firstName ? 'border-red-500' : ''}
                />
                {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <Label htmlFor="lastName" className="text-sm">Cognome *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  onBlur={checkDuplicates}
                  placeholder="Rossi"
                  className={errors.lastName ? 'border-red-500' : ''}
                />
                {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
              </div>
              <div>
                <Label htmlFor="birthDate" className="text-sm">Data di Nascita</Label>
                <Input
                  id="birthDate"
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => handleChange('birthDate', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="birthPlace" className="text-sm">Luogo di Nascita</Label>
                <Input
                  id="birthPlace"
                  value={formData.birthPlace}
                  onChange={(e) => handleChange('birthPlace', e.target.value)}
                  placeholder="Roma (RM)"
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sezione Contatti */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection('contatti')}
        >
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-green-600" />
            <h3 className="font-semibold text-sm">Contatti</h3>
          </div>
          {expandedSections.contatti ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.contatti && (
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="email" className="text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={checkDuplicates}
                  placeholder="mario.rossi@email.it"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm">Telefono</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  onBlur={checkDuplicates}
                  placeholder="+39 123 456 7890"
                  className={errors.phone ? 'border-red-500' : ''}
                />
                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="whatsappNumber" className="text-sm">Numero WhatsApp</Label>
                <Input
                  id="whatsappNumber"
                  type="tel"
                  value={formData.whatsappNumber}
                  onChange={(e) => handleChange('whatsappNumber', e.target.value)}
                  placeholder="+39 123 456 7890"
                  className={errors.whatsappNumber ? 'border-red-500' : ''}
                />
                {errors.whatsappNumber && <p className="text-xs text-red-500 mt-1">{errors.whatsappNumber}</p>}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sezione Dati Fiscali */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection('fiscale')}
        >
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-purple-600" />
            <h3 className="font-semibold text-sm">Dati Fiscali</h3>
          </div>
          {expandedSections.fiscale ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.fiscale && (
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="fiscalCode" className="text-sm">Codice Fiscale</Label>
                <Input
                  id="fiscalCode"
                  value={formData.fiscalCode}
                  onChange={(e) => handleChange('fiscalCode', e.target.value.toUpperCase())}
                  placeholder="RSSMRA80A01H501Z"
                  maxLength={16}
                  className={errors.fiscalCode ? 'border-red-500' : ''}
                />
                {errors.fiscalCode && <p className="text-xs text-red-500 mt-1">{errors.fiscalCode}</p>}
              </div>
              <div>
                <Label htmlFor="vatNumber" className="text-sm">Partita IVA</Label>
                <Input
                  id="vatNumber"
                  value={formData.vatNumber}
                  onChange={(e) => handleChange('vatNumber', e.target.value)}
                  placeholder="12345678901"
                  maxLength={11}
                  className={errors.vatNumber ? 'border-red-500' : ''}
                />
                {errors.vatNumber && <p className="text-xs text-red-500 mt-1">{errors.vatNumber}</p>}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sezione Residenza */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection('residenza')}
        >
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-orange-600" />
            <h3 className="font-semibold text-sm">Residenza</h3>
          </div>
          {expandedSections.residenza ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.residenza && (
          <CardContent className="p-4 space-y-3">
            <div>
              <Label htmlFor="address" className="text-sm">Indirizzo</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Via Roma, 123"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="col-span-2">
                <Label htmlFor="city" className="text-sm">Città</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Milano"
                />
              </div>
              <div>
                <Label htmlFor="province" className="text-sm">Provincia</Label>
                <Input
                  id="province"
                  value={formData.province}
                  onChange={(e) => handleChange('province', e.target.value.toUpperCase())}
                  placeholder="MI"
                  maxLength={2}
                />
              </div>
              <div>
                <Label htmlFor="postalCode" className="text-sm">CAP</Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={(e) => handleChange('postalCode', e.target.value)}
                  placeholder="20100"
                  maxLength={5}
                  className={errors.postalCode ? 'border-red-500' : ''}
                />
                {errors.postalCode && <p className="text-xs text-red-500 mt-1">{errors.postalCode}</p>}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sezione Note */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection('note')}
        >
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-600" />
            <h3 className="font-semibold text-sm">Note</h3>
          </div>
          {expandedSections.note ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.note && (
          <CardContent className="p-4">
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Note aggiuntive..."
              rows={3}
            />
          </CardContent>
        )}
      </Card>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onSuccess} disabled={isSubmitting}>
          Annulla
        </Button>
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
          {isSubmitting ? 'Salvataggio...' : (editData?.id ? 'Aggiorna' : 'Salva Cliente')}
        </Button>
      </div>
    </form>
  );
}

// ============================================
// FORM CLIENTE AZIENDA
// ============================================
function CompanyClientForm({ onSuccess, editData }: {
  onSuccess: () => void;
  editData?: EditClientData;
}) {
  const [formData, setFormData] = useState({
    // Dati azienda
    companyName: editData?.companyName || '',
    vatNumber: editData?.vatNumber || '',
    fiscalCode: editData?.fiscalCode || '',
    sdiCode: editData?.sdiCode || '',
    pec: editData?.pec || '',
    // Sede legale
    legalAddress: editData?.legalAddress || '',
    legalCity: editData?.legalCity || '',
    legalProvince: editData?.legalProvince || '',
    legalPostalCode: editData?.legalPostalCode || '',
    // Sede operativa
    operationalAddress: editData?.operationalAddress || '',
    operationalCity: editData?.operationalCity || '',
    operationalProvince: editData?.operationalProvince || '',
    operationalPostalCode: editData?.operationalPostalCode || '',
    // Rappresentante legale
    legalRepFirstName: editData?.legalRepFirstName || '',
    legalRepLastName: editData?.legalRepLastName || '',
    legalRepFiscalCode: editData?.legalRepFiscalCode || '',
    // Contatti
    email: editData?.email || '',
    phone: editData?.phone || '',
    whatsappNumber: editData?.whatsappNumber || '',
    // Note
    notes: editData?.notes || '',
    // firstName e lastName per compatibilità (riferimento interno)
    firstName: editData?.firstName || '',
    lastName: editData?.lastName || '',
  });

  const [expandedSections, setExpandedSections] = useState({
    azienda: true,
    sedeLegale: true,
    sedeOperativa: false,
    rappresentante: false,
    contatti: true,
    note: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sameAddress, setSameAddress] = useState(false);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const validateVatNumber = (vat: string): boolean => {
    if (!vat) return true;
    const regex = /^[0-9]{11}$/;
    return regex.test(vat);
  };

  const validateEmail = (email: string): boolean => {
    if (!email) return true;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateSdiCode = (sdi: string): boolean => {
    if (!sdi) return true;
    // SDI può essere 7 caratteri alfanumerici o "0000000" per privati
    const regex = /^[A-Z0-9]{7}$/i;
    return regex.test(sdi);
  };

  const handleCopyAddress = () => {
    setSameAddress(!sameAddress);
    if (!sameAddress) {
      setFormData(prev => ({
        ...prev,
        operationalAddress: prev.legalAddress,
        operationalCity: prev.legalCity,
        operationalProvince: prev.legalProvince,
        operationalPostalCode: prev.legalPostalCode,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!formData.companyName.trim()) newErrors.companyName = 'Ragione sociale obbligatoria';
    if (!formData.vatNumber.trim()) newErrors.vatNumber = 'Partita IVA obbligatoria';
    if (formData.vatNumber && !validateVatNumber(formData.vatNumber)) newErrors.vatNumber = 'Partita IVA non valida (11 cifre)';
    if (!formData.legalAddress.trim()) newErrors.legalAddress = 'Indirizzo sede legale obbligatorio';
    if (!formData.legalCity.trim()) newErrors.legalCity = 'Città sede legale obbligatoria';
    if (formData.email && !validateEmail(formData.email)) newErrors.email = 'Email non valida';
    if (formData.pec && !validateEmail(formData.pec)) newErrors.pec = 'PEC non valida';
    if (formData.sdiCode && !validateSdiCode(formData.sdiCode)) newErrors.sdiCode = 'Codice SDI non valido (7 caratteri)';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Correggi gli errori nel form');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('accessToken');
      const payload: any = {
        clientType: 'COMPANY',
        // Per aziende, firstName e lastName sono la ragione sociale
        firstName: formData.companyName.trim(),
        lastName: 'Azienda',
        companyName: formData.companyName.trim(),
        vatNumber: formData.vatNumber.trim(),
      };

      // Campi opzionali
      if (formData.fiscalCode) payload.fiscalCode = formData.fiscalCode.toUpperCase().trim();
      if (formData.sdiCode) payload.sdiCode = formData.sdiCode.toUpperCase().trim();
      if (formData.pec) payload.pec = formData.pec.trim();
      
      // Sede legale
      if (formData.legalAddress) payload.legalAddress = formData.legalAddress.trim();
      if (formData.legalCity) payload.legalCity = formData.legalCity.trim();
      if (formData.legalProvince) payload.legalProvince = formData.legalProvince.toUpperCase().trim();
      if (formData.legalPostalCode) payload.legalPostalCode = formData.legalPostalCode.trim();
      
      // Sede operativa
      if (formData.operationalAddress) payload.operationalAddress = formData.operationalAddress.trim();
      if (formData.operationalCity) payload.operationalCity = formData.operationalCity.trim();
      if (formData.operationalProvince) payload.operationalProvince = formData.operationalProvince.toUpperCase().trim();
      if (formData.operationalPostalCode) payload.operationalPostalCode = formData.operationalPostalCode.trim();
      
      // Rappresentante legale
      if (formData.legalRepFirstName) payload.legalRepFirstName = formData.legalRepFirstName.trim();
      if (formData.legalRepLastName) payload.legalRepLastName = formData.legalRepLastName.trim();
      if (formData.legalRepFiscalCode) payload.legalRepFiscalCode = formData.legalRepFiscalCode.toUpperCase().trim();
      
      // Contatti
      if (formData.email) payload.email = formData.email.trim();
      if (formData.phone) payload.phone = formData.phone.trim();
      if (formData.whatsappNumber) payload.whatsappNumber = formData.whatsappNumber.trim();
      
      // Note
      if (formData.notes) payload.notes = formData.notes.trim();

      const url = editData?.id ? `/api/clients/${editData.id}` : '/api/clients';
      const method = editData?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(editData?.id ? 'Azienda aggiornata con successo!' : 'Azienda creata con successo!');
        onSuccess();
      } else {
        if (data.errors) {
          let fieldErrors: Record<string, string> = {};
          if (Array.isArray(data.errors)) {
            data.errors.forEach((error: string) => {
              if (error.includes('companyName') || error.includes('Ragione')) fieldErrors.companyName = error;
              else if (error.includes('vatNumber') || error.includes('Partita IVA')) fieldErrors.vatNumber = error;
              else if (error.includes('email') || error.includes('Email')) fieldErrors.email = error;
              else if (error.includes('pec') || error.includes('PEC')) fieldErrors.pec = error;
              else if (error.includes('sdiCode') || error.includes('SDI')) fieldErrors.sdiCode = error;
              else fieldErrors.general = error;
            });
          } else {
            fieldErrors = data.errors;
          }
          setErrors(fieldErrors);
          toast.warning('Correggi gli errori evidenziati');
        } else {
          toast.error(data.message || 'Errore nella creazione dell\'azienda');
        }
      }
    } catch (error) {
      console.error('Error saving company:', error);
      toast.error('Errore nella creazione dell\'azienda');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Se sameAddress è attivo, copia anche all'operativo
    if (sameAddress && field.startsWith('legal')) {
      const operationalField = field.replace('legal', 'operational');
      setFormData(prev => ({ ...prev, [operationalField]: value }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-4">
        <Building2 className="mx-auto h-10 w-10 text-purple-500 mb-2" />
        <p className="text-gray-600 text-sm">
          {editData?.id ? 'Modifica i dati dell\'azienda' : 'Inserisci i dati della nuova azienda'}
        </p>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-sm">{errors.general}</p>
        </div>
      )}

      {/* Sezione Dati Azienda */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-purple-50 hover:bg-purple-100"
          onClick={() => toggleSection('azienda')}
        >
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-purple-600" />
            <h3 className="font-semibold text-sm">Dati Azienda</h3>
            <Badge variant="destructive" className="text-xs">Obbligatorio</Badge>
          </div>
          {expandedSections.azienda ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.azienda && (
          <CardContent className="p-4 space-y-3">
            <div>
              <Label htmlFor="companyName" className="text-sm">Ragione Sociale *</Label>
              <Input
                id="companyName"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                placeholder="Azienda S.r.l."
                className={errors.companyName ? 'border-red-500' : ''}
              />
              {errors.companyName && <p className="text-xs text-red-500 mt-1">{errors.companyName}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="vatNumber" className="text-sm">Partita IVA *</Label>
                <Input
                  id="vatNumber"
                  value={formData.vatNumber}
                  onChange={(e) => handleChange('vatNumber', e.target.value)}
                  placeholder="12345678901"
                  maxLength={11}
                  className={errors.vatNumber ? 'border-red-500' : ''}
                />
                {errors.vatNumber && <p className="text-xs text-red-500 mt-1">{errors.vatNumber}</p>}
              </div>
              <div>
                <Label htmlFor="fiscalCode" className="text-sm">Codice Fiscale</Label>
                <Input
                  id="fiscalCode"
                  value={formData.fiscalCode}
                  onChange={(e) => handleChange('fiscalCode', e.target.value.toUpperCase())}
                  placeholder="12345678901 (se diverso da P.IVA)"
                  maxLength={16}
                />
              </div>
              <div>
                <Label htmlFor="sdiCode" className="text-sm">Codice SDI</Label>
                <Input
                  id="sdiCode"
                  value={formData.sdiCode}
                  onChange={(e) => handleChange('sdiCode', e.target.value.toUpperCase())}
                  placeholder="XXXXXXX"
                  maxLength={7}
                  className={errors.sdiCode ? 'border-red-500' : ''}
                />
                {errors.sdiCode && <p className="text-xs text-red-500 mt-1">{errors.sdiCode}</p>}
              </div>
              <div>
                <Label htmlFor="pec" className="text-sm">PEC</Label>
                <Input
                  id="pec"
                  type="email"
                  value={formData.pec}
                  onChange={(e) => handleChange('pec', e.target.value)}
                  placeholder="azienda@pec.it"
                  className={errors.pec ? 'border-red-500' : ''}
                />
                {errors.pec && <p className="text-xs text-red-500 mt-1">{errors.pec}</p>}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sezione Sede Legale */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection('sedeLegale')}
        >
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-blue-600" />
            <h3 className="font-semibold text-sm">Sede Legale</h3>
            <Badge variant="destructive" className="text-xs">Obbligatorio</Badge>
          </div>
          {expandedSections.sedeLegale ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.sedeLegale && (
          <CardContent className="p-4 space-y-3">
            <div>
              <Label htmlFor="legalAddress" className="text-sm">Indirizzo *</Label>
              <Input
                id="legalAddress"
                value={formData.legalAddress}
                onChange={(e) => handleChange('legalAddress', e.target.value)}
                placeholder="Via Roma, 123"
                className={errors.legalAddress ? 'border-red-500' : ''}
              />
              {errors.legalAddress && <p className="text-xs text-red-500 mt-1">{errors.legalAddress}</p>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="col-span-2">
                <Label htmlFor="legalCity" className="text-sm">Città *</Label>
                <Input
                  id="legalCity"
                  value={formData.legalCity}
                  onChange={(e) => handleChange('legalCity', e.target.value)}
                  placeholder="Milano"
                  className={errors.legalCity ? 'border-red-500' : ''}
                />
                {errors.legalCity && <p className="text-xs text-red-500 mt-1">{errors.legalCity}</p>}
              </div>
              <div>
                <Label htmlFor="legalProvince" className="text-sm">Provincia</Label>
                <Input
                  id="legalProvince"
                  value={formData.legalProvince}
                  onChange={(e) => handleChange('legalProvince', e.target.value.toUpperCase())}
                  placeholder="MI"
                  maxLength={2}
                />
              </div>
              <div>
                <Label htmlFor="legalPostalCode" className="text-sm">CAP</Label>
                <Input
                  id="legalPostalCode"
                  value={formData.legalPostalCode}
                  onChange={(e) => handleChange('legalPostalCode', e.target.value)}
                  placeholder="20100"
                  maxLength={5}
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sezione Sede Operativa */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection('sedeOperativa')}
        >
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-orange-600" />
            <h3 className="font-semibold text-sm">Sede Operativa</h3>
            <span className="text-xs text-gray-500">(opzionale)</span>
          </div>
          {expandedSections.sedeOperativa ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.sedeOperativa && (
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="sameAddress"
                checked={sameAddress}
                onChange={handleCopyAddress}
                className="rounded"
              />
              <Label htmlFor="sameAddress" className="text-sm cursor-pointer">
                Coincide con sede legale
              </Label>
            </div>
            {!sameAddress && (
              <>
                <div>
                  <Label htmlFor="operationalAddress" className="text-sm">Indirizzo</Label>
                  <Input
                    id="operationalAddress"
                    value={formData.operationalAddress}
                    onChange={(e) => handleChange('operationalAddress', e.target.value)}
                    placeholder="Via Milano, 456"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="col-span-2">
                    <Label htmlFor="operationalCity" className="text-sm">Città</Label>
                    <Input
                      id="operationalCity"
                      value={formData.operationalCity}
                      onChange={(e) => handleChange('operationalCity', e.target.value)}
                      placeholder="Roma"
                    />
                  </div>
                  <div>
                    <Label htmlFor="operationalProvince" className="text-sm">Provincia</Label>
                    <Input
                      id="operationalProvince"
                      value={formData.operationalProvince}
                      onChange={(e) => handleChange('operationalProvince', e.target.value.toUpperCase())}
                      placeholder="RM"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="operationalPostalCode" className="text-sm">CAP</Label>
                    <Input
                      id="operationalPostalCode"
                      value={formData.operationalPostalCode}
                      onChange={(e) => handleChange('operationalPostalCode', e.target.value)}
                      placeholder="00100"
                      maxLength={5}
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        )}
      </Card>

      {/* Sezione Rappresentante Legale */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection('rappresentante')}
        >
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-green-600" />
            <h3 className="font-semibold text-sm">Rappresentante Legale</h3>
            <span className="text-xs text-gray-500">(opzionale)</span>
          </div>
          {expandedSections.rappresentante ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.rappresentante && (
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="legalRepFirstName" className="text-sm">Nome</Label>
                <Input
                  id="legalRepFirstName"
                  value={formData.legalRepFirstName}
                  onChange={(e) => handleChange('legalRepFirstName', e.target.value)}
                  placeholder="Mario"
                />
              </div>
              <div>
                <Label htmlFor="legalRepLastName" className="text-sm">Cognome</Label>
                <Input
                  id="legalRepLastName"
                  value={formData.legalRepLastName}
                  onChange={(e) => handleChange('legalRepLastName', e.target.value)}
                  placeholder="Rossi"
                />
              </div>
              <div>
                <Label htmlFor="legalRepFiscalCode" className="text-sm">Codice Fiscale</Label>
                <Input
                  id="legalRepFiscalCode"
                  value={formData.legalRepFiscalCode}
                  onChange={(e) => handleChange('legalRepFiscalCode', e.target.value.toUpperCase())}
                  placeholder="RSSMRA80A01H501Z"
                  maxLength={16}
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sezione Contatti */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection('contatti')}
        >
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-green-600" />
            <h3 className="font-semibold text-sm">Contatti</h3>
          </div>
          {expandedSections.contatti ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.contatti && (
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="email" className="text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="info@azienda.it"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm">Telefono</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+39 02 12345678"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="whatsappNumber" className="text-sm">WhatsApp Business</Label>
                <Input
                  id="whatsappNumber"
                  type="tel"
                  value={formData.whatsappNumber}
                  onChange={(e) => handleChange('whatsappNumber', e.target.value)}
                  placeholder="+39 123 456 7890"
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sezione Note */}
      <Card>
        <div
          className="p-3 cursor-pointer flex items-center justify-between bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection('note')}
        >
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-600" />
            <h3 className="font-semibold text-sm">Note</h3>
          </div>
          {expandedSections.note ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
        {expandedSections.note && (
          <CardContent className="p-4">
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Note aggiuntive sull'azienda..."
              rows={3}
            />
          </CardContent>
        )}
      </Card>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onSuccess} disabled={isSubmitting}>
          Annulla
        </Button>
        <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={isSubmitting}>
          {isSubmitting ? 'Salvataggio...' : (editData?.id ? 'Aggiorna' : 'Salva Azienda')}
        </Button>
      </div>
    </form>
  );
}
